import * as React from 'react';
import {
  Controller,
  type Control,
  type FieldValues,
  type UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import { composeRefs } from '../internals/composeRefs';
import { FileUploadZone } from '../composites/FileUploadZone';
import { FormField } from '../patterns/FormField';

import { rhfInlineError } from './rhfInlineError';
import type { RhfLayoutProps } from './rhfLayout';

export interface RhfFileUploadConfig {
  multiple: boolean;
  maxFiles: number | null;
  existingIdField?: string;
  existingNameField?: string;
  removedIdsField?: string;
}

export interface RhfFileUploadProps extends RhfLayoutProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  semanticId?: string;
  disabled?: boolean;
  accept?: string;
  maxSize?: number;
  fileType?: string;
  fileUpload: RhfFileUploadConfig;
  setValue: UseFormSetValue<FieldValues>;
}

function normalizeStagedFiles(value: unknown): File[] {
  if (value == null) return [];
  if (value instanceof File) return [value];
  if (Array.isArray(value)) {
    return value.filter((item): item is File => item instanceof File);
  }
  if (typeof value === 'object' && 'length' in value) {
    const list = value as FileList;
    return Array.from(list).filter((item): item is File => item instanceof File);
  }
  return [];
}

export const RhfFileUpload = React.forwardRef<HTMLInputElement, RhfFileUploadProps>(
  function RhfFileUpload(
    {
      control,
      name,
      label,
      hint,
      required = false,
      semanticId,
      disabled = false,
      accept,
      fileType,
      fileUpload,
      setValue,
      controlWidth,
    },
    ref,
  ) {
    const uploadConfig: RhfFileUploadConfig = {
      multiple: fileUpload?.multiple !== false,
      maxFiles: fileUpload?.maxFiles ?? null,
      existingIdField: fileUpload?.existingIdField,
      existingNameField: fileUpload?.existingNameField,
      removedIdsField: fileUpload?.removedIdsField,
    };

    const existingId = useWatch({
      control,
      name: uploadConfig.existingIdField ?? name,
      disabled: !uploadConfig.existingIdField,
    });
    const existingName = useWatch({
      control,
      name: uploadConfig.existingNameField ?? name,
      disabled: !uploadConfig.existingNameField,
    });
    const removedIdsRaw = useWatch({
      control,
      name: uploadConfig.removedIdsField ?? name,
      disabled: !uploadConfig.removedIdsField,
    });
    const removedIds = Array.isArray(removedIdsRaw)
      ? removedIdsRaw.filter((id): id is string => typeof id === 'string')
      : [];

    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });
          const stagedFiles = normalizeStagedFiles(field.value);

          const existingIdStr = typeof existingId === 'string' ? existingId : '';
          const existingNameStr = typeof existingName === 'string' ? existingName : '';
          const showExisting =
            existingIdStr &&
            existingNameStr &&
            !removedIds.includes(existingIdStr);

          const existingFiles = showExisting
            ? [{ id: existingIdStr, name: existingNameStr }]
            : [];

          const handleAdd = (added: File[]): void => {
            if (added.length === 0) return;
            let next = uploadConfig.multiple
              ? [...stagedFiles, ...added]
              : added.slice(0, 1);
            if (uploadConfig.maxFiles != null) {
              const slotsAvailable = Math.max(0, uploadConfig.maxFiles - existingFiles.length);
              next = next.slice(0, slotsAvailable);
            }
            if (next.length === 0) return;
            field.onChange(next);
          };

          const handleRemoveStaged = (index: number): void => {
            field.onChange(stagedFiles.filter((_, i) => i !== index));
          };

          const handleRemoveExisting = (id: string): void => {
            if (!uploadConfig.removedIdsField) return;
            if (removedIds.includes(id)) return;
            setValue(uploadConfig.removedIdsField, [...removedIds, id], { shouldDirty: true });
          };

          return (
            <FormField
              label={label}
              hint={hint}
              required={required}
              semanticId={semanticId}
              disabled={disabled}
              error={errorText}
              controlWidth={controlWidth}
            >
              <FileUploadZone
                inputRef={composeRefs(field.ref, ref)}
                semanticId={semanticId}
                name={field.name}
                stagedFiles={stagedFiles}
                existingFiles={existingFiles}
                onAdd={handleAdd}
                onRemoveStaged={handleRemoveStaged}
                onRemoveExisting={
                  uploadConfig.removedIdsField ? handleRemoveExisting : undefined
                }
                multiple={uploadConfig.multiple}
                maxFiles={uploadConfig.maxFiles}
                accept={accept}
                fileType={fileType}
                disabled={disabled}
                error={Boolean(errorText)}
                onBlur={field.onBlur}
              />
            </FormField>
          );
        }}
      />
    );
  },
);

RhfFileUpload.displayName = 'RhfFileUpload';
